// lib/client/interactionCache.js
'use client';

const CACHE_KEY = 'heart_interactions_cache';
const GUEST_COOKIE_NAME = 'poh_guest_saved';
const CACHE_VERSION = 1;
const CACHE_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes
const GUEST_COOKIE_MAX_AGE = 60 * 60 * 24 * 30; // 30 days

// Module-level state
let cache = {
  savedHearts: new Set(),
  visitedHearts: new Map(),
  lastSync: null,
  userId: null,
};

// Guest (non-authenticated) saved hearts
let guestSavedHearts = new Set();

const subscribers = new Set();

function emit(event) {
  subscribers.forEach((handler) => {
    try {
      handler(event);
    } catch (error) {
      console.warn('Interaction cache subscriber failed:', error);
    }
  });
}

function normalizeHeartId(heartId) {
  const parsed = parseInt(heartId, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Cookie utility functions for guest saved hearts
 */
function getCookieValue(name) {
  if (typeof document === 'undefined') return null;
  const parts = document.cookie.split('; ').find((part) => part.startsWith(`${name}=`));
  if (!parts) return null;
  return decodeURIComponent(parts.split('=').slice(1).join('='));
}

function setCookieValue(name, value, maxAge = GUEST_COOKIE_MAX_AGE) {
  if (typeof document === 'undefined') return;
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(
    value
  )}; Path=/; Max-Age=${maxAge}; SameSite=Lax${secure}`;
}

function safeParseCookie(json) {
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/**
 * Load cache from localStorage
 */
function loadFromStorage() {
  try {
    const stored = localStorage.getItem(CACHE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed.version === CACHE_VERSION && parsed.data) {
        cache = {
          savedHearts: new Set(parsed.data.savedHearts || []),
          visitedHearts: new Map(),
          lastSync: parsed.data.lastSync ? new Date(parsed.data.lastSync) : null,
          userId: parsed.data.userId || null,
        };

        // Handle visited hearts - support both array format and legacy object format
        if (Array.isArray(parsed.data.visitedHearts)) {
          // New array format: [{ heartId, VisitedAt }, ...]
          parsed.data.visitedHearts.forEach((visit) => {
            const heartId = parseInt(visit.heartId, 10);
            const timestamp = visit.VisitedAt || visit.visitedAt || visit.timestamp;
            if (heartId && timestamp) {
              cache.visitedHearts.set(heartId, timestamp);
            }
          });
        } else if (parsed.data.visitedHearts && typeof parsed.data.visitedHearts === 'object') {
          // Legacy object format: { [heartId]: timestamp }
          Object.entries(parsed.data.visitedHearts).forEach(([id, timestamp]) => {
            const heartId = parseInt(id, 10);
            if (heartId && timestamp) {
              cache.visitedHearts.set(heartId, timestamp);
            }
          });
        }
      }
    }
  } catch (error) {
    console.warn('Failed to load interaction cache from storage:', error);
  }

  // Load guest saved hearts from cookie
  try {
    const guestCookie = getCookieValue(GUEST_COOKIE_NAME);
    if (guestCookie) {
      const parsed = safeParseCookie(guestCookie);
      if (Array.isArray(parsed)) {
        guestSavedHearts = new Set(parsed.map((id) => parseInt(id, 10)));
      }
    }
  } catch (error) {
    console.warn('Failed to load guest saved hearts from cookie:', error);
  }
}

/**
 * Save cache to localStorage
 */
function saveToStorage() {
  try {
    // Convert visited hearts Map to array format
    const visitedHeartsArray = [];
    cache.visitedHearts.forEach((timestamp, heartId) => {
      visitedHeartsArray.push({
        heartId: parseInt(heartId, 10),
        VisitedAt: timestamp,
      });
    });

    const toStore = {
      version: CACHE_VERSION,
      data: {
        savedHearts: Array.from(cache.savedHearts),
        visitedHearts: visitedHeartsArray,
        lastSync: cache.lastSync?.toISOString() || null,
        userId: cache.userId,
      },
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(toStore));
  } catch (error) {
    console.warn('Failed to save interaction cache to storage:', error);
  }
}

/**
 * Save guest saved hearts to cookie
 */
function saveGuestToStorage() {
  try {
    const heartIds = Array.from(guestSavedHearts);
    setCookieValue(GUEST_COOKIE_NAME, JSON.stringify(heartIds));
  } catch (error) {
    console.warn('Failed to save guest saved hearts to cookie:', error);
  }
}

/**
 * Initialize cache with user data
 */
export function initializeCache(userId, data) {
  cache.userId = userId;
  // Ensure all saved heartIds are integers
  const savedHearts = (data.savedHearts || []).map((id) => parseInt(id, 10));
  cache.savedHearts = new Set(savedHearts);
  // Handle visited hearts - can be array format [{ heartId, VisitedAt }] or object format { [heartId]: timestamp }
  let visitedEntries = [];
  if (Array.isArray(data.visitedHearts)) {
    // Array format from REST adapter: [{ heartId, VisitedAt }, ...]
    visitedEntries = data.visitedHearts.map((visit) => [
      parseInt(visit.heartId, 10),
      visit.VisitedAt || visit.visitedAt || visit.timestamp,
    ]);
  } else {
    // Object format from memory adapter (legacy): { [heartId]: timestamp }
    visitedEntries = Object.entries(data.visitedHearts || {}).map(([id, timestamp]) => [
      parseInt(id, 10),
      timestamp,
    ]);
  }
  cache.visitedHearts = new Map(visitedEntries);
  cache.lastSync = new Date();
  saveToStorage();
  emit({ type: 'init' });
}

/**
 * Check if cache is valid for current user
 */
export function isCacheValid(userId) {
  if (cache.userId !== userId) return false;
  if (!cache.lastSync) return false;
  const age = Date.now() - cache.lastSync.getTime();
  return age < CACHE_EXPIRY_MS;
}

/**
 * Check if heart is saved
 */
export function isSaved(heartId) {
  heartId = normalizeHeartId(heartId);
  if (heartId === null) return false;
  return cache.savedHearts.has(heartId);
}

/**
 * Save a heart
 */
export function saveHeart(heartId) {
  heartId = normalizeHeartId(heartId);
  if (heartId === null) return;
  cache.savedHearts.add(heartId);
  saveToStorage();
  emit({ type: 'save', heartId });
}

/**
 * Unsave a heart
 */
export function unsaveHeart(heartId) {
  heartId = normalizeHeartId(heartId);
  if (heartId === null) return;
  cache.savedHearts.delete(heartId);
  saveToStorage();
  emit({ type: 'unsave', heartId });
}

/**
 * Check if heart is visited
 */
export function isVisited(heartId) {
  heartId = normalizeHeartId(heartId);
  if (heartId === null) return false;
  return cache.visitedHearts.has(heartId);
}

/**
 * Get visit timestamp
 */
export function getVisitTimestamp(heartId) {
  heartId = normalizeHeartId(heartId);
  if (heartId === null) return null;
  return cache.visitedHearts.get(heartId) || null;
}

/**
 * Mark heart as visited
 */
export function markVisited(heartId, timestamp) {
  heartId = normalizeHeartId(heartId);
  if (heartId === null) return;
  // Only set if not already visited (keep earliest timestamp)
  if (!cache.visitedHearts.has(heartId)) {
    cache.visitedHearts.set(heartId, timestamp);
    saveToStorage();
    emit({ type: 'visit', heartId, timestamp });
  }
}

/**
 * Remove visit from cache (for rollback)
 */
export function removeVisit(heartId) {
  heartId = normalizeHeartId(heartId);
  if (heartId === null) return;
  cache.visitedHearts.delete(heartId);
  saveToStorage();
  emit({ type: 'visit_remove', heartId });
}

/**
 * Get all saved hearts
 */
export function getAllSaved() {
  return Array.from(cache.savedHearts);
}

/**
 * Get all visited hearts
 * @returns {Array<{heartId: number, VisitedAt: string}>}
 */
export function getAllVisited() {
  const result = [];
  cache.visitedHearts.forEach((timestamp, heartId) => {
    result.push({
      heartId: parseInt(heartId, 10),
      VisitedAt: timestamp,
    });
  });
  return result;
}

/**
 * Guest (non-authenticated) functions
 */

/**
 * Check if heart is saved by guest
 */
export function isGuestSaved(heartId) {
  heartId = normalizeHeartId(heartId);
  if (heartId === null) return false;
  return guestSavedHearts.has(heartId);
}

/**
 * Save a heart as guest
 */
export function saveHeartAsGuest(heartId) {
  heartId = normalizeHeartId(heartId);
  if (heartId === null) return;
  guestSavedHearts.add(heartId);
  saveGuestToStorage();
  emit({ type: 'guest_save', heartId });
}

/**
 * Unsave a heart as guest
 */
export function unsaveHeartAsGuest(heartId) {
  heartId = normalizeHeartId(heartId);
  if (heartId === null) return;
  guestSavedHearts.delete(heartId);
  saveGuestToStorage();
  emit({ type: 'guest_unsave', heartId });
}

/**
 * Get all guest saved hearts
 */
export function getAllGuestSaved() {
  return Array.from(guestSavedHearts);
}

/**
 * Clear guest saved hearts
 */
export function clearGuestSaved() {
  guestSavedHearts.clear();
  try {
    // Clear cookie by setting Max-Age to 0
    setCookieValue(GUEST_COOKIE_NAME, '', 0);
  } catch (error) {
    console.warn('Failed to clear guest saved hearts cookie:', error);
  }
  emit({ type: 'guest_clear' });
}

/**
 * Sync guest saved hearts to authenticated user
 * Returns the array of heart IDs that were synced
 */
export function syncGuestSavedToAuth() {
  const guestHearts = Array.from(guestSavedHearts);

  // Add all guest saved hearts to authenticated cache
  guestHearts.forEach((heartId) => {
    cache.savedHearts.add(heartId);
  });

  // Clear guest cache
  guestSavedHearts.clear();

  // Save both caches
  saveToStorage();
  saveGuestToStorage();

  emit({ type: 'guest_sync', heartIds: guestHearts });

  return guestHearts;
}

/**
 * Clear all cache data
 */
export function clearCache() {
  cache = {
    savedHearts: new Set(),
    visitedHearts: new Map(),
    lastSync: null,
    userId: null,
  };
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch (error) {
    console.warn('Failed to clear interaction cache from storage:', error);
  }
  emit({ type: 'clear' });
}

/**
 * Update last sync time
 */
export function updateSyncTime() {
  cache.lastSync = new Date();
  saveToStorage();
  emit({ type: 'sync' });
}

export function subscribe(handler) {
  if (typeof handler !== 'function') return () => {};
  subscribers.add(handler);
  return () => {
    subscribers.delete(handler);
  };
}

// Initialize on module load
if (typeof window !== 'undefined') {
  loadFromStorage();
}
